import { VAction, VendorObjectType } from '@/interfaces/event-log';
import { eventTrack } from '@/lib/event-track';
import { VModuleKey } from 'interfaces/module';
import { CallType, CompleteStatus, IClientSocket, IVideoCallInfo } from 'interfaces/socket';
import vHistory, { VPathKey } from 'lib/history';
import vModule from 'lib/module';
import webSocket from 'lib/socket';
import { action, toJS } from 'mobx';
import qs from 'query-string';
import clientStore from './client-store';
import langStore from './language';
import { ssm, StoreObservable } from './store-manager';

const WEB_SOCKET_KEY = 'VKYC_WEB_SOCKET';
const DEFAULT_STATUS = CompleteStatus.SORRY;
const DEFAULT_MEDIA_ACCESS_COUNTDOWN = 15;

class SocketStore {
  private static CLIENT_SOCKET_DEFAULT: IClientSocket = {
    completeStatus: DEFAULT_STATUS,
    mediaAccessCountdown: DEFAULT_MEDIA_ACCESS_COUNTDOWN,
    mediaAccessDialogClosed: false,
    waitingNumber: '--',
    videoCallInfo: null,
    videoCallStart: false,
    userConnectedBefore: false,
    clientReady: false,
    consentAgreed: false, // Has user agreed the user consent
    callType: CallType.IN, // is doing outbound call
  };

  private _mediaAccessTimer = 0;

  private clientSocketStore: StoreObservable<IClientSocket>;

  constructor() {
    this.clientSocketStore = ssm.getStoreObservable<IClientSocket>(WEB_SOCKET_KEY);
    this.clientSocketStore.setDefault(() => SocketStore.CLIENT_SOCKET_DEFAULT);
  }

  public getSocketData(): IClientSocket {
    return this.clientSocketStore.getNotNull();
  }

  // begin media access countdown
  resetMediaAccessCountdown = () => {
    this.clearMediaAccessTimer();
    this.updateMediaAccessCountdown(this.getDefaultMediaAccessCountdown());
  };

  getDefaultMediaAccessCountdown = () => {
    const config = vModule.getModuleConfig(VModuleKey.main);
    return config?.mediaAccessCountdown ?? DEFAULT_MEDIA_ACCESS_COUNTDOWN;
  };

  updateClientReady = (data: boolean) => {
    this.clientSocketStore.setMember((v) => {
      v.clientReady = data;
    });
  };

  initMediaAccessCountdownTimer = () => {
    this._mediaAccessTimer = setTimeout(() => {
      if (this.clientSocketStore.getNotNull().mediaAccessDialogClosed) {
        this.resetMediaAccessCountdown();
      } else if (this.getSocketData().mediaAccessCountdown < 1) {
        const details = {
          name: 'NO_RESPONSE_TO_ACCESS_DIALOG',
          message: 'Access Dialog Reply Timeout',
        };
        eventTrack.pushEvent({
          action: VAction.OPENTOK_FAILED,
          extra: {
            sessionId: socketStore.getSocketData().videoCallInfo?.sessionId,
            type: VendorObjectType.OTHERS,
            details: details,
          },
        });
        webSocket.clientSendVendorErrorLeave(details);
        this.resetMediaAccessCountdown();
      } else {
        this.updateMediaAccessCountdown(this.getSocketData().mediaAccessCountdown - 1);
        this.initMediaAccessCountdownTimer();
      }
    }, 1000);
  };

  clearMediaAccessTimer = () => {
    this._mediaAccessTimer && clearTimeout(this._mediaAccessTimer);
  };

  updateMediaAccessCountdown = (data: number) => {
    this.clientSocketStore.setMember((v) => {
      v.mediaAccessCountdown = data;
    });
  };

  updateMediaAccessDialogClosed = (data: boolean) => {
    this.clientSocketStore.setMember((v) => {
      v.mediaAccessDialogClosed = data;
    });
  };
  // end media access countdown

  updateCallType = (type: CallType) => {
    this.clientSocketStore.setMember((v) => {
      v.callType = type;
    });
  };

  setToOutbound = () => {
    this.updateCallType(CallType.OUT);
  };

  setToInbound = () => {
    this.updateCallType(CallType.IN);
  };

  isOutbound = () => {
    return this.clientSocketStore.getNotNull().callType === CallType.OUT;
  };

  getCallType = () => {
    return this.clientSocketStore.getNotNull().callType;
  };

  hasVideoStarted = () => {
    return this.clientSocketStore.getNotNull().videoCallStart === true;
  };

  setStatusToSorry = () => {
    this.updateCompleteStatus(CompleteStatus.SORRY);
  };

  setStatusToMissed = () => {
    this.updateCompleteStatus(CompleteStatus.MISSED);
  };

  setStatusToAgentIssue = () => {
    this.updateCompleteStatus(CompleteStatus.AGENTISSUE);
  };

  setStatusToThankYou = () => {
    this.updateCompleteStatus(CompleteStatus.THANKYOU);
  };

  ifStatusIsThankYou = () => {
    return this.clientSocketStore.getNotNull().completeStatus === CompleteStatus.THANKYOU;
  };

  @action
  updateUserConnectedBefore = (data: boolean) => {
    this.getSocketData().userConnectedBefore = data;
  };

  updateVideoCallInfo = (data: IVideoCallInfo) => {
    this.clientSocketStore.setMember((v) => {
      v.videoCallInfo = data;
    });
  };

  updateVideoCallStart = (data: boolean) => {
    this.clientSocketStore.setMember((v) => {
      v.videoCallStart = data;
    });
  };

  updateCompleteStatus = (data: CompleteStatus) => {
    this.clientSocketStore.setMember((v) => {
      v.completeStatus = data;
    });
  };

  updateConsentAgreed = (data: boolean) => {
    this.clientSocketStore.setMember((v) => {
      v.consentAgreed = data;
    });
  };

  // from session storage
  getConsentAgreedFromSS = () => {
    return this.clientSocketStore.getNotNull().consentAgreed;
  };

  getWaitingNumber = () => {
    return this.clientSocketStore.getNotNull().waitingNumber;
  };

  updateWaitingNumber = (payload: number) => {
    this.clientSocketStore.setMember((v) => {
      v.waitingNumber = payload;
    });
  };

  clearData = () => {
    this.clientSocketStore.clear();
  };

  goToComplete = () => {
    if (vHistory.currentPathIs(VPathKey.COMPLETE) || location.pathname === '/') {
      return;
    }

    const currentUrl = decodeURIComponent(clientStore.getSession()?.returnUrl || '');
    const currentLanguage = langStore.getLan();
    const currentStatus = this.clientSocketStore.get()?.completeStatus || '';

    const queries = {
      status: currentStatus,
      url: encodeURIComponent(currentUrl),
      language: currentLanguage,
    };

    console.log('socket complete handle: ', queries);
    vHistory.toPageByKey(VPathKey.COMPLETE, `?${qs.stringify(queries)}`);
  };
}
const socketStore = new SocketStore();
export default socketStore;

['development', 'staging'].includes(process.env.MODE!) &&
  Object.assign(window, { socketStore, toJS });
